/**
 * RigStateManager - the Rig State library modal (PLAN.md QUEUED "Rig
 * States (visual recall)"). The authoring UI the engine (Milestones 1+2,
 * 2026-08-15) has been missing since it shipped: a bank list, a bank
 * editor (its looks), and a look editor (its captured targets).
 *
 * Three-level navigation inside ONE modal, same shape as TimelineEditor's
 * manager-list-vs-editor toggle: bank list -> bank editor -> look editor.
 * Reuses the shared `sk-modal`/`sk-srow`/`sk-chip`/`inspector-*` chassis
 * throughout (DEC-129: no per-skin structural CSS) - this file adds zero
 * new CSS.
 *
 * ## Targets are added explicitly, by picking (2026-08-15 decision)
 *
 * Not a "start recording, auto-capture whatever gets touched" mode - the
 * "+ Add target" picker lists the 4 `liveState/registry.ts` named targets
 * (transpose/capo/Waves key+scale) plus anything currently in the generic
 * MIDI send-cache, and tapping one captures its live value into the
 * currently-open look right then (`addTargetToLook`). Session-ephemeral by
 * construction for the generic-MIDI half - a target never sent this
 * session can't appear yet, same limitation the send-cache design already
 * accepted (see `liveState/sendCache.ts`'s file header).
 *
 * ## "Recall" here is a live test-fire, not a preview
 *
 * The look editor's Recall chip calls the real `recallLook` - same
 * function a placed `RigStateButtonItem` calls. There is no separate
 * dry-run path; authoring and testing share one mechanism on purpose.
 */

import { createSignal, createMemo, For, Show, onMount, onCleanup } from 'solid-js';
import type { RigStateBank, RigStateLook } from '@opbuttons/schema';
import {
  rigStateBanks, getBank, bankUnion,
  createBank, removeBank, renameBank, duplicateBank,
  captureLook, removeLook, renameLook, setHomeLook, duplicateLook,
  addTargetToLook, removeTargetFromLook, recaptureLookTarget,
  recallLook,
} from '../rigState/store.ts';
import { LIVE_TARGETS } from '../liveState/registry.ts';
import { listSendCacheTargets } from '../liveState/sendCache.ts';
import { targetLabel, recallSummary } from '../rigState/targetWords.ts';
import { pushToast } from '../ui/toast.ts';


export function RigStateManager() {
  const [selectedBankId, setSelectedBankId] = createSignal<string | null>(null);
  const [selectedLookId, setSelectedLookId] = createSignal<string | null>(null);
  const [addTargetOpen, setAddTargetOpen] = createSignal(false);

  const selectedBank = createMemo<RigStateBank | null>(() => {
    const id = selectedBankId();
    return id ? getBank(id) ?? null : null;
  });
  const selectedLook = createMemo<RigStateLook | null>(() => {
    const b = selectedBank();
    const id = selectedLookId();
    return b && id ? (b.looks.find((l) => l.id === id) ?? null) : null;
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (addTargetOpen()) { setAddTargetOpen(false); return; }
    if (selectedLookId()) { setSelectedLookId(null); return; }
    if (selectedBankId()) setSelectedBankId(null);
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  /** Enter/Space activation for the div-based navigable rows below - they
   *  can't be real `<button>`s because each one also carries its own
   *  nested action chip (Delete/Recall), and a button can't contain
   *  another interactive element. */
  function onRowKey(e: KeyboardEvent, activate: () => void): void {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
  }

  function onCreateBank(): void {
    const bank = createBank(`Rig State ${rigStateBanks().length + 1}`);
    setSelectedBankId(bank.id);
  }
  /** Delete asks with a second tap, as Lights does (2026-10-16: this was the
   *  browser's own popup). The first tap arms it ("Really delete X?"); it
   *  disarms by itself after 3 s. Rig States have no undo, so it stays. */
  const [armedDelete, setArmedDelete] = createSignal<string | null>(null);
  let disarmTimer: ReturnType<typeof setTimeout> | undefined;
  function armOrRun(id: string, run: () => void): void {
    clearTimeout(disarmTimer);
    if (armedDelete() === id) { setArmedDelete(null); run(); return; }
    setArmedDelete(id);
    disarmTimer = setTimeout(() => setArmedDelete(null), 3000);
  }
  onCleanup(() => clearTimeout(disarmTimer));
  function onDuplicateBank(bank: RigStateBank): void {
    const copy = duplicateBank(bank.id);
    if (copy) pushToast('ok', `Made "${copy.name}"`, 2000);
  }
  function onDuplicateLook(look: RigStateLook): void {
    const b = selectedBank();
    if (!b) return;
    const copy = duplicateLook(b.id, look.id);
    if (copy) pushToast('ok', `Made "${copy.name}"`, 2000);
  }
  function onDeleteBank(bank: RigStateBank): void {
    removeBank(bank.id);
    if (selectedBankId() === bank.id) setSelectedBankId(null);
  }
  function onCreateLook(): void {
    const b = selectedBank();
    if (!b) return;
    const look = captureLook(b.id, `Look ${b.looks.length + 1}`);
    if (look) setSelectedLookId(look.id);
  }
  function onDeleteLook(look: RigStateLook): void {
    const b = selectedBank();
    if (!b) return;
    removeLook(b.id, look.id);
    if (selectedLookId() === look.id) setSelectedLookId(null);
  }
  async function onTestRecall(bankId: string, lookId: string, name: string): Promise<void> {
    const r = recallSummary(name, await recallLook(bankId, lookId));
    pushToast(r.tone, r.text, r.tone === 'error' ? 5000 : 2200);
  }

  /** What the "+ Add target" picker offers: registry targets with a live
   *  value the look doesn't already carry, plus everything in the generic
   *  send-cache the look doesn't already carry. */
  const addable = createMemo(() => {
    const look = selectedLook();
    if (!look) return { registry: [] as typeof LIVE_TARGETS, cache: [] as ReturnType<typeof listSendCacheTargets> };
    const already = new Set(look.values.map((v) => v.targetId));
    return {
      registry: LIVE_TARGETS.filter((t) => !already.has(t.id) && t.read() !== null),
      cache: listSendCacheTargets().filter((t) => !already.has(t.targetId)),
    };
  });

  // The section's content, shared by its own window and its pane in Navigate
  // (2026-10-05, app-study.html: every section opens inside Navigate).
  const body = () => (
    <>

          {/* ── Level 1: the Rig States ──────────────────────────────── */}
          <Show when={!selectedBank()}>
            <div class="nv-row">
              <button type="button" class="nv-b nv-b-pri" onClick={onCreateBank}>New Rig State</button>
            </div>
            <section class="nv-ss">
              <div class="nv-ss-h">
                <h2>Your Rig States</h2>
                <p>Each holds a few looks. Capture a look, then put a Rig State button on any page aimed at it: the "instant chord" between one Button and a whole Timeline.</p>
              </div>
              <Show when={rigStateBanks().length > 0} fallback={<p class="nav-empty">No Rig States yet.</p>}>
                <div class="nav-well">
                  <For each={rigStateBanks()}>
                    {(bank) => (
                      <div
                        class="nav-lr nav-lr-button rig-bank-row"
                        role="button"
                        tabindex="0"
                        onClick={() => setSelectedBankId(bank.id)}
                        onKeyDown={(e) => onRowKey(e, () => setSelectedBankId(bank.id))}
                      >
                        <span class="nav-lr-main">
                          <b>{bank.name}</b>
                          <small>
                            {bank.looks.length} look{bank.looks.length === 1 ? '' : 's'} · {bankUnion(bank).length} target{bankUnion(bank).length === 1 ? '' : 's'}
                          </small>
                        </span>
                        <span class="nav-lr-trail">
                          <button type="button" class="nv-b nv-b-sm" data-testid="rig-duplicate" onClick={(e) => { e.stopPropagation(); onDuplicateBank(bank); }}>
                            Duplicate
                          </button>
                          <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="rig-delete" data-danger={armedDelete() === bank.id ? 'armed' : 'true'} onClick={(e) => { e.stopPropagation(); armOrRun(bank.id, () => onDeleteBank(bank)); }}>
                            {armedDelete() === bank.id ? `Really delete ${bank.name}?` : 'Delete'}
                          </button>
                          <span class="nav-lr-chev" aria-hidden="true">›</span>
                        </span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </section>
          </Show>

          {/* ── Level 2: a Rig State's looks ─────────────────────────── */}
          <Show when={selectedBank() && !selectedLook()}>
            {(() => {
              const b = selectedBank()!;
              return (
                <>
                  <label class="nv-field rig-name">
                    <span>Name</span>
                    <input type="text" value={b.name} onChange={(e) => renameBank(b.id, e.currentTarget.value)} />
                  </label>
                  <section class="nv-ss">
                    <div class="nv-ss-h">
                      <h2>Looks</h2>
                      <p>Open one to capture what it sets.</p>
                      <span class="nv-sp" />
                      <button type="button" class="nv-b nv-b-sm" onClick={onCreateLook}>New look</button>
                    </div>
                    <Show when={b.looks.length > 0} fallback={<p class="nav-empty">No looks yet. Add one, then open it to capture targets.</p>}>
                      <div class="nav-well">
                        <For each={b.looks}>
                          {(look) => (
                            <div
                              class="nav-lr nav-lr-button rig-look-row"
                              role="button"
                              tabindex="0"
                              onClick={() => setSelectedLookId(look.id)}
                              onKeyDown={(e) => onRowKey(e, () => setSelectedLookId(look.id))}
                            >
                              <span class="nav-lr-main">
                                <b>{look.name}<Show when={look.isHome}> <span class="nv-ct" data-s="ok">Home</span></Show></b>
                                <small>{look.values.length} target{look.values.length === 1 ? '' : 's'}</small>
                              </span>
                              <span class="nav-lr-trail">
                                <button
                                  type="button"
                                  class="nv-b nv-b-sm"
                                  onClick={(e) => { e.stopPropagation(); void onTestRecall(b.id, look.id, look.name); }}
                                >
                                  Recall
                                </button>
                                <button type="button" class="nv-b nv-b-sm" data-testid="look-duplicate" onClick={(e) => { e.stopPropagation(); onDuplicateLook(look); }}>
                                  Duplicate
                                </button>
                                <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="look-delete" data-danger={armedDelete() === look.id ? 'armed' : 'true'} onClick={(e) => { e.stopPropagation(); armOrRun(look.id, () => onDeleteLook(look)); }}>
                                  {armedDelete() === look.id ? `Really delete ${look.name}?` : 'Delete'}
                                </button>
                                <span class="nav-lr-chev" aria-hidden="true">›</span>
                              </span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </section>
                </>
              );
            })()}
          </Show>

          {/* ── Level 3: a look's captured targets ───────────────────── */}
          <Show when={selectedBank() && selectedLook()}>
            {(() => {
              const b = selectedBank()!;
              const l = selectedLook()!;
              return (
                <>
                  <div class="nv-row rig-look-head">
                    <label class="nv-field rig-name">
                      <span>Name</span>
                      <input type="text" value={l.name} onChange={(e) => renameLook(b.id, l.id, e.currentTarget.value)} />
                    </label>
                    <Show
                      when={l.isHome}
                      fallback={<button type="button" class="nv-b" onClick={() => setHomeLook(b.id, l.id)}>Make this the home look</button>}
                    >
                      <span class="nv-ct" data-s="ok">The home look</span>
                    </Show>
                  </div>
                  <section class="nv-ss">
                    <div class="nv-ss-h">
                      <h2>What it sets</h2>
                      <span class="nv-sp" />
                      <button type="button" class="nv-b nv-b-sm" aria-expanded={addTargetOpen()} onClick={() => setAddTargetOpen((v) => !v)}>
                        {addTargetOpen() ? 'Close' : 'Add a target'}
                      </button>
                    </div>
                    <Show when={l.values.length > 0} fallback={<p class="nav-empty">Nothing captured yet.</p>}>
                      <div class="nav-well">
                        <For each={l.values}>
                          {(entry) => (
                            <div class="nav-lr">
                              <span class="nav-lr-main">
                                <b>{targetLabel(entry.targetId)}</b>
                                <small>{String(entry.value)}</small>
                              </span>
                              <span class="nav-lr-trail">
                                <button type="button" class="nv-b nv-b-sm" onClick={() => recaptureLookTarget(b.id, l.id, entry.targetId)}>
                                  Capture again
                                </button>
                                <button type="button" class="nv-b nv-b-q nv-b-sm" data-danger="true" onClick={() => removeTargetFromLook(b.id, l.id, entry.targetId)}>
                                  Remove
                                </button>
                              </span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </section>
                  <Show when={addTargetOpen()}>
                    <section class="nv-ss" data-nav-layer>
                      <div class="nv-ss-h"><h2>Add a target</h2><p>What is live right now, with its current value.</p></div>
                      <Show
                        when={addable().registry.length > 0 || addable().cache.length > 0}
                        fallback={<p class="nav-empty">Nothing live to capture. Set a value on the rig, or send a MIDI CC once this session, then try again.</p>}
                      >
                        <div class="nav-well">
                          <For each={addable().registry}>
                            {(t) => (
                              <button
                                type="button"
                                class="nav-lr nav-lr-button"
                                onClick={() => { addTargetToLook(b.id, l.id, t.id); setAddTargetOpen(false); }}
                              >
                                <span class="nav-lr-main"><b>{t.label}</b><small>{t.read()?.display}</small></span>
                                <span class="nav-lr-chev" aria-hidden="true">+</span>
                              </button>
                            )}
                          </For>
                          <For each={addable().cache}>
                            {(t) => (
                              <button
                                type="button"
                                class="nav-lr nav-lr-button"
                                onClick={() => { addTargetToLook(b.id, l.id, t.targetId); setAddTargetOpen(false); }}
                              >
                                <span class="nav-lr-main"><b>{targetLabel(t.targetId)}</b><small>{t.raw ?? Math.round(t.value * 127)}</small></span>
                                <span class="nav-lr-chev" aria-hidden="true">+</span>
                              </button>
                            )}
                          </For>
                        </div>
                      </Show>
                    </section>
                  </Show>
                </>
              );
            })()}
          </Show>
    </>
  );
  // Inside a Rig State or a look, the section keeps its way back up: the same
  // back link as Help's, and data-nav-layer so Escape steps back first.
  return (
    <div class="nav-pane-body rig-pane" role="region" aria-label="Rig States">
      <Show when={selectedBankId()}>
        <div class="nav-pane-crumb" data-nav-layer>
          <button
            type="button"
            class="logo-menu-pane-back nv-back-always"
            onClick={() => { setSelectedLookId(null); setAddTargetOpen(false); if (selectedLookId() === null) setSelectedBankId(null); }}
          >
            {selectedLookId() ? `‹ ${selectedBank()?.name ?? 'Rig State'}` : '‹ Rig States'}
          </button>
          <span class="nav-pane-crumb-title">{selectedLook()?.name ?? selectedBank()?.name}</span>
        </div>
      </Show>
      {body()}
    </div>
  );
}
